import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'
import { useRefDep } from 'hooks/ref-dep'
import { animationFrameScheduler, fromEvent } from 'rxjs'
import {
  map,
  observeOn,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { clamp } from 'utils/math-util'

/**
 * Props for the `useDraggable` hook.
 */
export interface UseDraggableOptions {
  /**
   * Position the element should be when mounted.
   */
  initialPosition?: {
    x: number
    y: number
  }
  /**
   * Called before unmount with last position.
   *
   * @param position
   */
  onSavePosition?: (position: { x: number; y: number }) => void
}

/**
 * Result from the `useDraggable` hook.
 */
export type UseDraggableResult<H, C> = [
  boolean,
  MutableRefObject<(H extends HTMLElement ? H : HTMLElement) | null>,
  MutableRefObject<(C extends HTMLElement ? C : HTMLElement) | null>
]

/**
 * Draggable element hook.
 *
 * @param draggableOptions Optional parameter allowing persistence of drag state if drag ref is removed from the DOM.
 * @returns A triple containing the drag state plus the drag ref and an optional container ref.
 */
export function useDraggable<H = HTMLElement, C = H>(
  draggableOptions?: UseDraggableOptions
): UseDraggableResult<H, C> {
  const [isDragging, setIsDragging] = useState(false)
  const position = useRef({ x: 0, y: 0 })
  // Returned to register the target for handling mousedown events.
  const dragHandleRef = useRef<
    (H extends HTMLElement ? H : HTMLElement) | null
  >(null)
  // Returned to register an encapsulating container to translate instead of just the drag "handle". Optional.
  const containerRef = useRef<(C extends HTMLElement ? C : HTMLElement) | null>(
    null
  )

  const updatePosition = useCallback((x: number, y: number) => {
    if (!dragHandleRef.current) {
      return
    }

    const elem = containerRef.current
      ? containerRef.current
      : dragHandleRef.current

    position.current = {
      x,
      y,
    }
    elem.style.transform = `translate(${position.current.x}px, ${position.current.y}px)`
  }, [])

  // Drag handling and positioning logic.
  useEffect(() => {
    if (!dragHandleRef.current) {
      return
    }

    const mouseDown$ = fromEvent<MouseEvent>(
      dragHandleRef.current,
      'mousedown'
    ).pipe(share())
    const mouseMove$ = fromEvent<MouseEvent>(document, 'mousemove').pipe(
      share()
    )
    const mouseUp$ = fromEvent<MouseEvent>(document, 'mouseup').pipe(share())

    const dragStart$ = mouseDown$.pipe(tap((down) => down.preventDefault()))
    const dragMove$ = mouseMove$.pipe(tap((move) => move.preventDefault()))
    const dragStop$ = mouseUp$.pipe(tap((stop) => stop.preventDefault()))

    const startSubscription = dragStart$
      .pipe(
        switchMap(() => dragMove$.pipe(takeUntil(dragStop$))),
        map((move) => ({
          movementX: move.movementX,
          movementY: move.movementY,
        })),
        observeOn(animationFrameScheduler)
      )
      .subscribe((move) => {
        if (!dragHandleRef.current) {
          return
        }

        setIsDragging(true)

        const elem = containerRef.current
          ? containerRef.current
          : dragHandleRef.current
        const elemRect = elem.getBoundingClientRect()
        const pos = position.current

        const topRestriction = pos.y - elemRect.top
        const bottomRestriction = window.innerHeight + pos.y - elemRect.bottom

        const leftRestriction = pos.x - elemRect.left
        const rightRestriction = window.innerWidth + pos.x - elemRect.right

        updatePosition(
          clamp(pos.x + move.movementX, leftRestriction, rightRestriction),
          clamp(pos.y + move.movementY, topRestriction, bottomRestriction)
        )
      })

    const stopSubscription = dragStop$.subscribe(() => {
      setIsDragging(false)
    })

    return () => {
      startSubscription.unsubscribe()
      stopSubscription.unsubscribe()
      if (!draggableOptions) {
        reset()
      }
    }
  }, [useRefDep(dragHandleRef)])

  // External control logic.
  useEffect(() => {
    if (!draggableOptions || !dragHandleRef.current) {
      return
    }

    const { initialPosition, onSavePosition } = draggableOptions

    // If an initial position is provided, update the starting position of the element.
    if (initialPosition) {
      updatePosition(initialPosition.x, initialPosition.y)
    }

    return () => {
      // If position is being preserved, update controlled state when unmounting.
      onSavePosition?.({
        x: position.current.x,
        y: position.current.y,
      })
    }
  }, [useRefDep(dragHandleRef)])

  const reset = () => {
    updatePosition(0, 0)
  }

  return [isDragging, dragHandleRef, containerRef]
}
